import { Container, Col, Button, Row, FloatingLabel, Form } from 'react-bootstrap';
import classes from './Playground.module.css';
import React, { useEffect, useState } from 'react';
import { ImageData, getBitstrayData, getRandomBitstraySeed } from '@bitstrays/assets';
import { buildSVG } from '@bitstrays/sdk';
import Bitstray from '../../components/Bitstray';
import BitstrayModal from './BitstrayModal';

interface Trait {
  title: string;
  traitNames: string[];
}

const parseTraitName = (partName: string): string =>
  capitalizeFirstLetter(partName.substring(partName.indexOf('-') + 1));

const capitalizeFirstLetter = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const Playground: React.FC = () => {
  const [bitstraySvgs, setBitstraySvgs] = useState<string[]>();
  const [traits, setTraits] = useState<Trait[]>();
  const [modSeed, setModSeed] = useState<{ [key: string]: number }>();
  const [initLoad, setInitLoad] = useState<boolean>(true);
  const [displayBitstray, setDisplayBitstray] = useState<boolean>(false);
  const [indexOfBitstrayToDisplay, setIndexOfBitstrayToDisplay] = useState<number>();

  const generateBitstraySvg = React.useCallback(
    (amount: number = 1) => {
      for (let i = 0; i < amount; i++) {
        const seed = { ...getRandomBitstraySeed(), ...modSeed };
        const { parts, background } = getBitstrayData(seed);
        const svg = buildSVG(parts, ImageData.palette, background);
        setBitstraySvgs(prev => {
          return prev ? [svg, ...prev] : [svg];
        });
      }
    },
    [modSeed],
  );

  useEffect(() => {
    const traitTitles = ['background', 'arms', 'shirt', 'motive', 'head', 'eyes', 'mouth'];
    const traitNames = [
      ['gray', 'beige', 'green', 'blue', 'creme', 'pink', 'purple'],
      ...Object.values(ImageData.images).map(i => {
        return i.map(imageData => imageData.filename);
      }),
    ];
    setTraits(
      traitTitles.map((value, index) => {
        return {
          title: value,
          traitNames: traitNames[index],
        };
      }),
    );

    if (initLoad) {
      generateBitstraySvg(12);
      setInitLoad(false);
    }
  }, [generateBitstraySvg, initLoad]);

  const traitOptions = (trait: Trait) => {
    return Array.from(Array(trait.traitNames.length + 1)).map((_, index) => {
      const parsedTitle = index === 0 ? `Random` : parseTraitName(trait.traitNames[index - 1]);
      return <option key={index}>{parsedTitle}</option>;
    });
  };

  const traitButtonHandler = (trait: Trait, traitIndex: number) => {
    setModSeed(prev => {
      // -1 traitIndex = random
      if (traitIndex < 0) {
        let state = { ...prev };
        delete state[trait.title];
        return state;
      }
      return {
        ...prev,
        [trait.title]: traitIndex,
      };
    });
  };

  return (
    <>
      {displayBitstray && indexOfBitstrayToDisplay !== undefined && bitstraySvgs && (
        <BitstrayModal
          onDismiss={() => {
            setDisplayBitstray(false);
          }}
          svg={bitstraySvgs[indexOfBitstrayToDisplay]}
        />
      )}

      <Container fluid="lg">
        <Row>
          <Col lg={10} className={classes.headerRow}>
            <span>Explore</span>
            <h1>Playground</h1>
            <p>
              Bitstray's traits are determined by the Bitstray Seed. While we can not
              generate any more Bitstrays this generator will help growing community
              and let people discover the traits.
            </p>
          </Col>
        </Row>
        <Row>
          <Col lg={3}>
            <Button
              onClick={() => {
                generateBitstraySvg();
              }}
              className={classes.generateBtn}
            >
              Generate Bitstrays
            </Button>
            {traits &&
              traits.map((trait, index) => {
                return (
                  <Form className={classes.traitForm}>
                    <FloatingLabel
                      controlId="floatingSelect"
                      label={capitalizeFirstLetter(trait.title)}
                      key={index}
                      className={classes.floatingLabel}
                    >
                      <Form.Select
                        aria-label="Floating label select example"
                        className={classes.traitFormBtn}
                        onChange={e => {
                          let index = e.currentTarget.selectedIndex;
                          traitButtonHandler(trait, index - 1); // - 1 to account for 'random'
                        }}
                      >
                        {traitOptions(trait)}
                      </Form.Select>
                    </FloatingLabel>
                  </Form>
                );
              })}
          </Col>
          <Col lg={9}>
            <Row>
              {bitstraySvgs &&
                bitstraySvgs.map((svg, i) => {
                  return (
                    <Col xs={4} lg={3} key={i}>
                      <div
                        onClick={() => {
                          setIndexOfBitstrayToDisplay(i);
                          setDisplayBitstray(true);
                        }}
                      >
                        <Bitstray
                          imgPath={`data:image/svg+xml;base64,${btoa(svg)}`}
                          alt="bitstray"
                          className={classes.bitstrayImg}
                          wrapperClassName={classes.bitstrayWrapper}
                        />
                      </div>
                    </Col>
                  );
                })}
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};
export default Playground;
